
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, Stars, Environment, Box, Line, Sparkles, Plane } from '@react-three/drei';
import type { Group, Mesh } from 'three';
import { CoinModel } from './models/CoinModel';

// Fix for React Three Fiber Intrinsic Elements in TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      group: any;
      mesh: any;
      meshStandardMaterial: any;
      meshBasicMaterial: any;
      pointLight: any;
      ambientLight: any;
      torusGeometry: any;
      gridHelper: any;
      color: any;
      fog: any;
      [elemName: string]: any;
    }
  }
  namespace React {
    namespace JSX {
      interface IntrinsicElements {
        group: any;
        mesh: any;
        meshStandardMaterial: any;
        meshBasicMaterial: any;
        pointLight: any;
        ambientLight: any;
        torusGeometry: any;
        gridHelper: any;
        color: any;
        fog: any;
        [elemName: string]: any;
      }
    }
  }
}

const MovingGrid = () => {
    const gridRef = useRef<Group>(null);
    useFrame((state) => {
        if (gridRef.current) {
            // Move grid towards camera
            gridRef.current.position.z = (state.clock.getElapsedTime() * 2) % 2;
        }
    });

    return (
        <group ref={gridRef} position={[0, -3, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <Plane args={[100, 100, 50, 50]}>
                <meshBasicMaterial color="#C5A059" wireframe transparent opacity={0.1} />
            </Plane>
        </group>
    );
};

const AbstractRing = () => {
  const ref = useRef<Mesh>(null);
  
  useFrame((state) => {
    if (ref.current) {
       const t = state.clock.getElapsedTime();
       ref.current.rotation.x = Math.sin(t * 0.2) * 0.2;
       ref.current.rotation.y = t * 0.1;
    }
  });

  return (
    <group>
        <torusGeometry args={[3.5, 0.02, 16, 100]} />
        <meshStandardMaterial color="#C5A059" emissive="#C5A059" emissiveIntensity={0.5} transparent opacity={0.3} />
    </group>
  );
}

const Embers = () => {
  return (
    <group>
      {/* Fiery sparks rising */}
      <Sparkles 
        count={80}
        scale={12}
        size={4}
        speed={0.4}
        opacity={0.6}
        color="#ff8800"
        noise={0.2}
      />
      {/* Floating particles */}
      {Array.from({ length: 20 }).map((_, i) => (
         <Float key={i} speed={1 + Math.random()} rotationIntensity={2} floatIntensity={2} floatingRange={[-2, 2]}>
             <Box args={[0.05, 0.05, 0.05]} position={[
                 (Math.random() - 0.5) * 12,
                 (Math.random() - 0.5) * 10,
                 (Math.random() - 0.5) * 8 - 2
             ]}>
                 <meshStandardMaterial 
                    color={Math.random() > 0.5 ? "#C5A059" : "#ff4d00"} 
                    emissive={Math.random() > 0.5 ? "#C5A059" : "#ff4d00"}
                    emissiveIntensity={2} 
                    transparent 
                    opacity={0.6}
                 />
             </Box>
         </Float>
      ))}
    </group>
  )
}

export const QuantumScene: React.FC = () => {
  return (
    <div className="absolute inset-0 z-0 opacity-100 pointer-events-none">
      <Canvas camera={{ position: [0, 0, 6], fov: 45 }}>
        {/* Atmosphere */}
        <color attach="background" args={['#F9F8F4']} />
        <fog attach="fog" args={['#F9F8F4', 5, 20]} />
        
        <ambientLight intensity={0.6} />
        <pointLight position={[10, 10, 10]} intensity={1.5} color="#ffffff" />
        <pointLight position={[-5, -5, 5]} intensity={1.5} color="#ff4d00" />
        
        <MovingGrid />

        <Float speed={1.5} rotationIntensity={0.2} floatIntensity={0.5}>
          <CoinModel position={[0, 0, 0]} />
          {/* Create a few abstract rings for depth */}
          <group rotation={[Math.PI / 3, 0, 0]}>
            <AbstractRing />
          </group>
          <group rotation={[-Math.PI / 3, 0, 0]} scale={0.8}>
            <AbstractRing />
          </group>
        </Float>
        
        <Embers />

        <Environment preset="city" />
        <Stars radius={100} depth={50} count={1500} factor={4} saturation={0} fade speed={1} />
      </Canvas>
    </div>
  );
};

// --- BLOCKCHAIN VISUALIZATION SCENE (PRESERVED) ---

const NetworkNode = ({ position, color = "#C5A059" }: { position: [number, number, number], color?: string }) => {
    return (
        <group position={position}>
            <Box args={[0.4, 0.4, 0.4]}>
                <meshStandardMaterial color="#1a1a1a" metalness={0.8} roughness={0.2} />
            </Box>
            <Box args={[0.42, 0.42, 0.42]}>
                <meshStandardMaterial color={color} wireframe transparent opacity={0.3} />
            </Box>
        </group>
    )
}

const NetworkConnection = ({ start, end }: { start: [number, number, number], end: [number, number, number] }) => {
    return (
        <Line 
            points={[start, end]} 
            color="#C5A059" 
            opacity={0.15} 
            transparent 
            lineWidth={1} 
        />
    )
}

const DistributedLedger = () => {
    const groupRef = useRef<Group>(null);
    
    // Create a lattice of nodes
    const nodes = useMemo(() => {
        const temp = [];
        const count = 5;
        const radius = 2.5;
        for (let i = 0; i < count; i++) {
             const theta = (i / count) * Math.PI * 2;
             const x = Math.cos(theta) * radius;
             const z = Math.sin(theta) * radius;
             temp.push({ x, y: Math.sin(i * 2) * 1, z });
        }
        // Add a center node
        temp.push({ x: 0, y: 0, z: 0 });
        return temp;
    }, []);

    useFrame((state) => {
        if (groupRef.current) {
            const t = state.clock.getElapsedTime();
            // Smoother rotation
            groupRef.current.rotation.y = t * 0.15;
            // Add a gentle floating wave motion
            groupRef.current.position.y = Math.sin(t * 0.5) * 0.5;
            // Slight tilt oscillation
            groupRef.current.rotation.z = Math.sin(t * 0.2) * 0.05;
        }
    });

    return (
        <group ref={groupRef}>
             {nodes.map((pos, i) => (
                 <group key={i}>
                    <NetworkNode position={[pos.x, pos.y, pos.z]} />
                    {/* Connect everyone to center */}
                    {i < nodes.length - 1 && (
                        <NetworkConnection start={[pos.x, pos.y, pos.z]} end={[0, 0, 0]} />
                    )}
                    {/* Connect to next neighbor for ring effect */}
                    {i < nodes.length - 1 && (
                        <NetworkConnection 
                            start={[pos.x, pos.y, pos.z]} 
                            end={[nodes[(i + 1) % (nodes.length - 1)].x, nodes[(i + 1) % (nodes.length - 1)].y, nodes[(i + 1) % (nodes.length - 1)].z]} 
                        />
                    )}
                 </group>
             ))}
             
             {/* Floating data packets */}
             {Array.from({ length: 8 }).map((_, i) => (
                 <Float key={`f-${i}`} speed={2} rotationIntensity={2} floatIntensity={2}>
                     <Box args={[0.1, 0.1, 0.1]} position={[
                         (Math.random() - 0.5) * 4,
                         (Math.random() - 0.5) * 4,
                         (Math.random() - 0.5) * 4
                     ]}>
                         <meshStandardMaterial color="#C5A059" emissive="#C5A059" emissiveIntensity={2} />
                     </Box>
                 </Float>
             ))}
        </group>
    );
}

export const QuantumComputerScene: React.FC = () => {
  return (
    <div className="w-full h-full absolute inset-0 bg-gradient-to-br from-stone-50 to-stone-100">
      <Canvas camera={{ position: [4, 4, 4], fov: 45 }}>
        <ambientLight intensity={0.8} />
        <pointLight position={[10, 10, 10]} intensity={1} color="#C5A059" />
        <pointLight position={[-10, -5, -5]} intensity={0.5} color="#4F46E5" />
        
        <DistributedLedger />
        
        <Environment preset="city" />
        {/* Subtle grid for tech feel */}
        <gridHelper args={[20, 20, 0xcccccc, 0xe5e5e5]} position={[0, -2, 0]} />
      </Canvas>
    </div>
  );
}
